/* global URL_MENU */
// `MouseEvent` is imported under an alias: this file already uses the DOM's global `MouseEvent` in
// its `document` listeners, and React's would shadow it.
import {
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  useCallback,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
} from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import DefaultImgBussiness from '@assets/images/DefaultImgBussiness.png';
import BtnMenu from '@assets/images/iconos-aside-panel-menu.svg';
import BtnORders from '@assets/images/iconos-aside-panel-orders.svg';
import BtnPubli from '@assets/images/iconos-aside-panel-anuncio.svg';
import BtnAjustes from '@assets/images/iconos-aside-panel-ajustes.svg';
import onlinePaymentIcon from '@assets/images/onlinePaymentIcon.svg';
import BtnQR from '@assets/images/iconos-aside-panel-qr.svg';
import CouponIcon from '@assets/images/iconos-aside-panel-coupons.svg';
import IconoQ from '@assets/images/icono-question.svg';
import IconoEstadisticas from '@assets/images/iconos-aside-panel-estadisticas.svg';
import BtnMesas from '@assets/images/iconos-aside-panel-comercio.svg';
import BtnProfile from '@assets/images/icono-profile.svg';
import ClientsIcon from '@assets/images/icono-mis clientes.svg';
import { useAuth } from '@context/AuthContext';
import { useGetCommerce } from '@pages/Commerce/hooks/commerce';
import useModules from '@utils/hooks/useModules';
import { useCanSwitchBranch } from '@utils/hooks/useCanSwitchBranch';
import NavBarBackground from './components/layout/NavBarBackground';
import NavBarContainerV2 from './components/layout/NavBarContainerV2';
import NavBarToggleButton from './components/interface/NavBarToggleButton';
import NavBarStatus from './components/interface/NavBarStatus';
import { ModuleName } from '@utils/constants';
import { useSubscription } from '@pages/Subscriptionsv2/hooks/useSubscriptions';
import Loading from '@components/LoadingProcess';
import { IconSettingsAutomation, IconDeviceTv, IconBrandWhatsapp } from '@tabler/icons-react';
import { useShouldShowSetup } from '@pages/Setup/hooks/useShouldShowSetup';
import { Icon } from '@iconify/react';
import Tooltip from '@components/interface/Tooltip';
import BranchPicker from '@components/v4/BranchPicker';
import { IconPointFilled } from '@tabler/icons-react';
import FounderContactModal from '@components/interface/FounderContactModal';
import FounderMessageForm from '@components/interface/FounderMessageForm';
import useTracking, { TRACK_EVENT_NAMES } from '@utils/hooks/useTracking';
import { useFeatureVersion } from '@hooks/useFeatureVersion';
import { useBlePrintingRollout } from '@hooks/useBlePrintingRollout';
import { MENU_FEATURE_KEY } from '@utils/featureRollout/constants';
import { useSidebarMode } from './useSidebarMode';

// Pill "Nuevo" para sub-items del nav (descubrimiento de features del rollout). Acento de
// marca, texto fijo: señala que hay una funcionalidad nueva en esa entrada, no un estado.
const NavNewPill = () => (
  <span
    style={{
      fontSize: 9,
      fontWeight: 800,
      letterSpacing: '.06em',
      textTransform: 'uppercase' as const,
      background: '#FEAE33',
      color: '#253746',
      borderRadius: 9999,
      padding: '2px 7px',
      flexShrink: 0,
      marginLeft: 6,
    }}
  >
    Nuevo
  </span>
);

const getNavigatePath = (hasModule: boolean, path: string) => {
  return hasModule ? path : 'suscripcion/' + path;
};

// ─── SVG del ícono pin de mapa ─────────────────────────────────────────────
const PinIcon = ({ size = 16, color = 'currentColor' }: { size?: number; color?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M12 22s8-5 8-12a8 8 0 1 0-16 0c0 7 8 12 8 12z" stroke={color} strokeWidth="2" strokeLinejoin="round" />
    <circle cx="12" cy="10" r="2.5" fill={color} />
  </svg>
);

// ─── SidebarBranchFooter ───────────────────────────────────────────────────
const CREAR_SUCURSAL_WA =
  'https://api.whatsapp.com/send?phone=5219935318237&text=Hola%2C%20quiero%20crear%20una%20nueva%20sucursal%20para%20mi%20comercio.';

type SidebarBranchFooterProps = {
  collapsed: boolean;
  isAccountLevel?: boolean;
};

// Estado + reglas compartidas por las dos superficies del switch (colapsado /
// expandido). Centraliza el gate de visibilidad/bloqueo para no duplicarlo.
const useBranchFooterState = (isAccountLevel: boolean) => {
  const { canSwitch, branches, activeBranchName, switchTo, isRoot } = useCanSwitchBranch();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  // Botón ancla del flyout colapsado (riel de iconos / tablet).
  const flyoutBtnRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  // "Crear sucursal" es una solicitud de ventas (WhatsApp): el dueño (root) puede verla
  // SIEMPRE, en todas las superficies, aunque no tenga multisucursal — upsell.
  const showCreate = isRoot;
  // El switch se MUESTRA SIEMPRE (a todos): indica la sucursal activa. Solo es interactivo
  // (dropdown para cambiar) cuando canSwitch (root + módulo + ≥2 sucursales); si no, queda
  // BLOQUEADO (visible pero sin opción de cambiar de sucursal).
  const showSwitch = isAccountLevel || Boolean(activeBranchName);
  const switchDisabled = isAccountLevel || !canSwitch;
  // Candado: el switch existe pero no puedes cambiar (sin root/módulo). No aplica
  // a account-level (ese estado es informativo, no gateado).
  const showLock = !isAccountLevel && !canSwitch;
  const switchTitle = isAccountLevel
    ? 'Esta sección no depende de sucursal'
    : canSwitch
      ? 'Cambiar sucursal'
      : 'El cambio de sucursal requiere el módulo multisucursal';

  return {
    canSwitch,
    branches,
    activeBranchName,
    switchTo,
    open,
    setOpen,
    ref,
    flyoutBtnRef,
    showCreate,
    showSwitch,
    switchDisabled,
    showLock,
    switchTitle,
  };
};

// ─── Riel colapsado (≥1280 manual · tablet 1024–1279 forzado) ──────────────
const CollapsedBranchFooter = ({ isAccountLevel = false }: { isAccountLevel?: boolean }) => {
  const {
    branches,
    activeBranchName,
    switchTo,
    open,
    setOpen,
    ref,
    flyoutBtnRef,
    showCreate,
    showSwitch,
    switchDisabled,
    showLock,
    switchTitle,
  } = useBranchFooterState(isAccountLevel);
  const flyoutRef = useRef<HTMLDivElement>(null);

  // El flyout usa position:fixed (escapa el overflow:hidden del sidebar), así que
  // su posición se ancla al botón y debe RECALCULARSE si el riel scrollea o cambia
  // el viewport — si no, queda visualmente desanclado (reportado por sentry[bot]).
  const positionFlyout = useCallback(() => {
    const el = flyoutRef.current;
    const btn = flyoutBtnRef.current;
    if (!el || !btn) return;
    const r = btn.getBoundingClientRect();
    el.style.left = `${r.right + 6}px`;
    el.style.top = `${r.top}px`;
  }, [flyoutBtnRef]);

  // useLayoutEffect (no useEffect): posiciona sincrónicamente antes del paint para
  // evitar el flicker de un frame en (0,0) al abrir (reportado por sentry[bot]).
  useLayoutEffect(() => {
    if (!open) return;
    positionFlyout();
    // capture=true para captar también el scroll del riel de navegación.
    window.addEventListener('scroll', positionFlyout, true);
    window.addEventListener('resize', positionFlyout);
    return () => {
      window.removeEventListener('scroll', positionFlyout, true);
      window.removeEventListener('resize', positionFlyout);
    };
  }, [open, positionFlyout]);

  // branches/permissions are populated asynchronously — returning early before
  // hooks causes a Rules of Hooks violation when canSwitch flips between renders.
  if (!showSwitch && !showCreate) return null;

  return (
    <div
      ref={ref}
      style={{
        padding: '10px',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        alignItems: 'center',
        background: '#ffffff',
      }}
    >
      {showSwitch && (
        <Tooltip
          label={isAccountLevel ? 'Esta sección no depende de sucursal' : `Sucursal: ${activeBranchName}`}
          enabled
        >
          <button
            ref={flyoutBtnRef}
            onClick={() => !switchDisabled && setOpen((o) => !o)}
            title={switchTitle}
            disabled={switchDisabled}
            style={{
              width: 36,
              height: 36,
              borderRadius: 8,
              background: switchDisabled ? '#EDF5F7' : '#4298B5',
              color: switchDisabled ? '#6B6B6B' : '#ffffff',
              border: switchDisabled ? '1px solid #E5ECEE' : 0,
              cursor: switchDisabled ? 'default' : 'pointer',
              display: 'grid',
              placeItems: 'center',
              position: 'relative',
            }}
          >
            <PinIcon size={16} color={switchDisabled ? '#6B6B6B' : '#ffffff'} />
            {showLock && (
              <span
                style={{
                  position: 'absolute',
                  right: -2,
                  bottom: -2,
                  width: 15,
                  height: 15,
                  borderRadius: '50%',
                  background: '#ffffff',
                  border: '1px solid #E5ECEE',
                  color: '#6B6B6B',
                  display: 'grid',
                  placeItems: 'center',
                }}
              >
                <svg width="8" height="8" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <rect x="4" y="10" width="16" height="11" rx="2" stroke="currentColor" strokeWidth="2.5" />
                  <path d="M8 10V7a4 4 0 0 1 8 0v3" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
                </svg>
              </span>
            )}
          </button>
        </Tooltip>
      )}
      {open && !switchDisabled && (
        <div
          ref={flyoutRef}
          style={{
            position: 'fixed',
            width: 240,
            background: '#ffffff',
            border: '1px solid #E5ECEE',
            borderRadius: 10,
            boxShadow: '0 12px 28px rgba(37,55,70,.18)',
            padding: 8,
            zIndex: 200,
          }}
        >
          <div
            style={{
              fontSize: 11,
              color: '#6B6B6B',
              textTransform: 'uppercase',
              letterSpacing: '.06em',
              padding: '0 2px 8px',
              fontWeight: 700,
            }}
          >
            Cambiar sucursal
          </div>
          <BranchPicker
            branches={branches}
            current={activeBranchName}
            variant="light"
            autoFocus
            maxHeight={260}
            onPick={(n) => {
              switchTo(n);
              setOpen(false);
            }}
          />
        </div>
      )}
      {showCreate && (
        <Tooltip label="Crear sucursal" enabled>
          <a
            href={CREAR_SUCURSAL_WA}
            target="_blank"
            rel="noopener noreferrer"
            title="Crear sucursal"
            style={{
              width: 36,
              height: 36,
              borderRadius: 8,
              background: '#EDF5F7',
              color: '#4298B5',
              border: '1px solid #E5ECEE',
              display: 'grid',
              placeItems: 'center',
              textDecoration: 'none',
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
            </svg>
          </a>
        </Tooltip>
      )}
    </div>
  );
};

// ─── Footer expandido (sidebar completo) ───────────────────────────────────
const ExpandedBranchFooter = ({ isAccountLevel = false }: { isAccountLevel?: boolean }) => {
  const {
    canSwitch,
    branches,
    activeBranchName,
    switchTo,
    open,
    setOpen,
    ref,
    showCreate,
    showSwitch,
    switchDisabled,
    switchTitle,
  } = useBranchFooterState(isAccountLevel);

  // branches/permissions are populated asynchronously — returning early before
  // hooks causes a Rules of Hooks violation when canSwitch flips between renders.
  if (!showSwitch && !showCreate) return null;

  return (
    <div
      ref={ref}
      style={{
        padding: '10px 12px 6px',
        background: '#ffffff',
        position: 'relative',
        borderBottom: '1px solid #E5ECEE',
        marginBottom: 6,
      }}
    >
      {showSwitch && (
        <>
          <button
            onClick={() => !switchDisabled && setOpen((o) => !o)}
            disabled={switchDisabled}
            title={switchTitle}
            style={{
              width: '100%',
              background: switchDisabled ? '#EDF5F7' : '#ffffff',
              border: '1px solid #E5ECEE',
              borderRadius: 10,
              padding: '8px 10px',
              cursor: switchDisabled ? 'default' : 'pointer',
              fontFamily: 'inherit',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              opacity: switchDisabled ? 0.85 : 1,
              textAlign: 'left',
            }}
          >
            <span
              style={{
                width: 36,
                height: 36,
                borderRadius: 8,
                background: switchDisabled ? '#BFD3D9' : '#4298B5',
                color: '#ffffff',
                display: 'grid',
                placeItems: 'center',
                flexShrink: 0,
              }}
            >
              <PinIcon size={16} color="#ffffff" />
            </span>
            <span
              style={{
                flex: 1,
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                lineHeight: 1.15,
              }}
            >
              <span
                style={{
                  fontSize: 10,
                  color: '#6B6B6B',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '.06em',
                }}
              >
                {isAccountLevel ? 'Aplica a tu cuenta' : 'Sucursal activa'}
              </span>
              <span
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: '#253746',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {isAccountLevel ? 'Toda la cuenta' : activeBranchName}
              </span>
            </span>
            {/* Chevron solo cuando se puede cambiar (canSwitch). Si está bloqueado, candado. */}
            {!switchDisabled ? (
              <span style={{ color: '#6B6B6B', flexShrink: 0 }}>
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  aria-hidden="true"
                  style={{
                    transition: 'transform .2s',
                    transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                  }}
                >
                  <path
                    d="M6 9l6 6 6-6"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
            ) : (
              !isAccountLevel && (
                <span style={{ color: '#6B6B6B', flexShrink: 0 }} title={switchTitle}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <rect x="5" y="11" width="14" height="9" rx="2" stroke="currentColor" strokeWidth="2" />
                    <path d="M8 11V8a4 4 0 0 1 8 0v3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                </span>
              )
            )}
          </button>

          {open && canSwitch && !isAccountLevel && (
            <div
              style={{
                position: 'absolute',
                top: 'calc(100% + 4px)',
                left: 10,
                right: 10,
                background: '#ffffff',
                border: '1px solid #E5ECEE',
                borderRadius: 10,
                boxShadow: '0 12px 28px rgba(37,55,70,.15)',
                padding: 8,
                zIndex: 200,
              }}
            >
              <BranchPicker
                branches={branches}
                current={activeBranchName}
                variant="light"
                autoFocus
                maxHeight={240}
                onPick={(n) => {
                  switchTo(n);
                  setOpen(false);
                }}
              />
            </div>
          )}
        </>
      )}

      {showCreate && (
        <a
          href={CREAR_SUCURSAL_WA}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            width: '100%',
            textAlign: 'left',
            padding: '8px 10px',
            marginTop: showSwitch ? 8 : 0,
            borderRadius: 8,
            background: 'transparent',
            color: '#4298B5',
            cursor: 'pointer',
            fontFamily: 'inherit',
            fontSize: 13,
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            textDecoration: 'none',
            boxSizing: 'border-box',
          }}
        >
          <span
            style={{
              width: 36,
              height: 36,
              borderRadius: 8,
              background: '#EDF5F7',
              display: 'grid',
              placeItems: 'center',
              flexShrink: 0,
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
            </svg>
          </span>
          Crear sucursal
        </a>
      )}
    </div>
  );
};

const SidebarBranchFooter = ({ collapsed, isAccountLevel = false }: SidebarBranchFooterProps) =>
  collapsed ? (
    <CollapsedBranchFooter isAccountLevel={isAccountLevel} />
  ) : (
    <ExpandedBranchFooter isAccountLevel={isAccountLevel} />
  );

type NavButtonV3Props = {
  id: string;
  to: string;
  icon: string;
  isExpanded: boolean;
  label: string;
  notIncludedOnCurrentPlan?: boolean;
  tablerIcon?: ReactNode;
  // Punto "nueva versión disponible": se muestra cuando el comercio es elegible para la
  // invitación del rollout y aún no la aceptó. Visible colapsado (sobre el ícono) y
  // expandido (junto al label). No-bloqueante: solo señaliza, el switch ocurre en el menú.
  showBadge?: boolean;
};

const NavButtonV3 = ({
  to,
  id,
  icon,
  isExpanded,
  label,
  notIncludedOnCurrentPlan = false,
  tablerIcon,
  showBadge = false,
}: NavButtonV3Props) => {
  return (
    <Tooltip label={label} enabled={!isExpanded}>
      <NavLink
        to={notIncludedOnCurrentPlan ? `suscripcion/${to}` : to}
        className={({ isActive }) =>
          `group relative flex items-center py-[10px] pr-4 pl-[13px] text-[14px] border-l-[3px] ${
            isActive
              ? 'text-color-white bg-color-secondary font-semibold'
              : 'text-color-primary font-medium hover:bg-[#EDF5F7]'
          }`
        }
        style={({ isActive }) => ({
          transition: 'color 160ms, transform 80ms',
          borderLeftColor: isActive ? '#2d6f86' : 'transparent',
        })}
        data-press=""
        id={id}
      >
        {({ isActive }) => (
          <>
            <div className="relative flex-shrink-0">
              {tablerIcon ? (
                <div className={`w-[18px] h-[18px] ${isActive ? 'brightness-0 invert' : ''}`}>{tablerIcon}</div>
              ) : (
                <img
                  src={icon}
                  alt=""
                  role="presentation"
                  className={`w-[18px] h-[18px] ${isActive ? 'brightness-0 invert' : ''}`}
                />
              )}
              {showBadge && (
                <span
                  aria-hidden="true"
                  style={{
                    position: 'absolute',
                    top: -3,
                    right: -3,
                    width: 8,
                    height: 8,
                    borderRadius: 9999,
                    background: '#1FC49A',
                    border: `1.5px solid ${isActive ? '#4298B5' : '#FFFFFF'}`,
                    boxShadow: '0 0 0 1px rgba(31,196,154,.35)',
                  }}
                />
              )}
            </div>
            <div
              className={`overflow-hidden transition-all ${isExpanded ? 'ml-3 w-full opacity-100' : 'w-0 opacity-0'}`}
            >
              <span className="whitespace-nowrap flex items-center gap-2">
                {label}
                {showBadge && (
                  <span
                    style={{
                      fontSize: 9,
                      fontWeight: 800,
                      letterSpacing: '.06em',
                      textTransform: 'uppercase',
                      color: isActive ? '#FFFFFF' : '#1FC49A',
                      background: isActive ? 'rgba(255,255,255,.18)' : 'rgba(31,196,154,.12)',
                      padding: '2px 6px',
                      borderRadius: 9999,
                    }}
                  >
                    Nuevo
                  </span>
                )}
              </span>
            </div>
            {notIncludedOnCurrentPlan && (
              <div className="ml-auto">
                <IconPointFilled className={isActive ? 'text-white' : 'text-color-secondary'} width={14} />
              </div>
            )}
          </>
        )}
      </NavLink>
    </Tooltip>
  );
};

// eslint-disable-next-line complexity
const NavBarDesktopV3 = () => {
  const navigate = useNavigate();
  // Responsive behaviour of the sidebar (see `useSidebarMode`): between 1024–1279 the icon rail is
  // the resting state and the toggle opens a temporary drawer that FLOATS over the content; from
  // 1280 up the merchant controls the collapse and the sidebar pushes the content as it always did.
  const { isCompactDesktop, isExpanded, isDrawerOpen, toggleSidebar, closeDrawer, toggleRef, panelRef, panelId } =
    useSidebarMode();
  const [isConfigOpen, setIsConfigOpen] = useState(true);
  const [settingsFlyoutOpen, setSettingsFlyoutOpen] = useState(false);
  const settingsBtnRef = useRef<HTMLButtonElement>(null);
  const { user, logout } = useAuth();
  const { isFreemium, planId } = useSubscription();
  const { commerce, isFetching } = useGetCommerce();
  const { hasModule } = useModules();
  const shouldShowSetup = useShouldShowSetup();
  // Elegibilidad del rollout `menu-v2` para señalizar el punto "nueva versión" en el item Menú.
  // ⚠️ NO copiar este patrón (useFeatureVersion crudo + hasModule en el call-site) para
  // features nuevas: usar el gate del contrato (makeFeatureGateHook) como impresion-ble abajo.
  const { isEligibleInvitation: menuRolloutEligible } = useFeatureVersion(MENU_FEATURE_KEY);
  // Elegibilidad del rollout `impresion-ble`: pill "Nuevo" en el sub-item Ajustes (acordeón y
  // flyout) + dot en el trigger colapsado. Solo control invitable con dispositivo compatible.
  const { inviteEligible: bleRolloutEligible } = useBlePrintingRollout();
  const [showFounderWarning, setShowFounderWarning] = useState(false);
  const [showFounderForm, setShowFounderForm] = useState(false);
  const { trackEvent } = useTracking();

  useEffect(() => {
    if (!settingsFlyoutOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (
        settingsBtnRef.current &&
        !settingsBtnRef.current.contains(e.target as Node) &&
        !(e.target as Element).closest('[data-settings-flyout]')
      ) {
        setSettingsFlyoutOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [settingsFlyoutOpen]);

  useEffect(() => {
    if (!isExpanded) return;
    setSettingsFlyoutOpen(false);
  }, [isExpanded]);

  const location = useLocation();
  const SETTINGS_PATHS = ['datos-del-comercio', 'horarios', 'tarifas', 'usuarios', 'ajustes', 'integraciones'];
  const isSubActive = (path: string) => location.pathname.includes(path);
  const isOnSettingsRoute = SETTINGS_PATHS.some((p) => isSubActive(p));

  useEffect(() => {
    if (isOnSettingsRoute) setIsConfigOpen(true);
  }, [isOnSettingsRoute]);

  // Picking a destination closes the temporary drawer. Resolved by delegation on the panel instead
  // of wiring an `onClick` into every item: any link counts as navigation, and so does any control
  // tagged with `data-nav-item` (the settings sub-items navigate through `navigate`). The "Ajustes"
  // accordion and the toggle itself are NOT tagged, so they never close the drawer.
  const handlePanelClick = (event: ReactMouseEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement | null;
    if (target?.closest('a[href], [data-nav-item]')) closeDrawer();
  };

  const handleGoToMenu = () => {
    if (!commerce.id_path) return;
    window.open(`${URL_MENU}/${commerce.id_path}`, '_blank');
  };

  return (
    <NavBarBackground
      isExpanded={isExpanded}
      isDrawerOpen={isDrawerOpen}
      panelId={panelId}
      panelRef={panelRef}
      onBackdropClick={closeDrawer}
      onPanelClick={handlePanelClick}
    >
      {/* The toggle is ALWAYS available. From 1280 up it expands/collapses the in-flow sidebar; in
         1024–1279 it opens the same menu as a temporary drawer over the content (without pushing). */}
      <NavBarToggleButton
        ref={toggleRef}
        isExpanded={isExpanded}
        isDrawer={isCompactDesktop}
        controlsId={panelId}
        onToggle={toggleSidebar}
      />
      {!planId ? (
        <Loading />
      ) : (
        <NavBarContainerV2>
          {/* Sección superior: Logo e información */}
          <div style={{ padding: '18px 16px 14px', borderBottom: '1px solid #E5ECEE', flexShrink: 0 }}>
            {isExpanded ? (
              <div className="flex items-center gap-3">
                <div className="relative flex-shrink-0">
                  <div
                    style={{
                      width: 52,
                      height: 52,
                      borderRadius: '50%',
                      border: '3px solid #253746',
                      overflow: 'hidden',
                    }}
                  >
                    <img
                      src={commerce.logotipo || DefaultImgBussiness}
                      alt="Logo del comercio"
                      className="w-full h-full object-cover bg-color-white"
                    />
                  </div>
                  <div className="absolute top-0 right-0 bg-color-white rounded-full p-0.5 shadow-md">
                    <NavBarStatus isOpen={commerce.servicio_activo ?? true} showLabel={false} size="md" />
                  </div>
                </div>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div
                    style={{
                      fontSize: 14,
                      fontWeight: 700,
                      color: '#253746',
                      lineHeight: 1.15,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {commerce.nombre}
                  </div>
                  <div style={{ fontSize: 12, color: '#6B6B6B', marginTop: 2, lineHeight: 1.2 }}>Bienvenido:</div>
                  <div style={{ fontSize: 13, color: '#253746', fontWeight: 700, lineHeight: 1.2 }}>
                    {user.username}
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex justify-center">
                <div className="relative">
                  <div
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      border: '3px solid #253746',
                      overflow: 'hidden',
                    }}
                  >
                    <img
                      src={commerce.logotipo || DefaultImgBussiness}
                      alt="Logo del comercio"
                      className="w-full h-full object-cover bg-color-white"
                    />
                  </div>
                  <div className="absolute top-0 right-0 bg-color-white rounded-full p-0.5 shadow-sm">
                    <NavBarStatus isOpen={commerce.servicio_activo ?? true} showLabel={false} size="sm" />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Sección media: botones con scroll */}
          <div className="flex-1 flex lg:flex-col overflow-y-auto overflow-x-hidden" style={{ padding: '8px 0' }}>
            <SidebarBranchFooter collapsed={!isExpanded} />
            {shouldShowSetup && (
              <NavButtonV3
                isExpanded={isExpanded}
                to="configuracion-inicial"
                id="configuracion-inicial-link"
                icon=""
                tablerIcon={<IconSettingsAutomation color="#4298b5" stroke={2} />}
                label="Configuración inicial"
              />
            )}

            {(isFreemium || hasModule(ModuleName.HISTORIAL)) && (
              <NavButtonV3
                isExpanded={isExpanded}
                to="control-pedidos"
                id="control-pedidos-link"
                icon={BtnORders}
                label="Control de pedidos"
                notIncludedOnCurrentPlan={!hasModule(ModuleName.HISTORIAL)}
              />
            )}

            {(isFreemium || hasModule(ModuleName.ESTADISTICAS)) && (
              <NavButtonV3
                isExpanded={isExpanded}
                to="estadisticas"
                id="estadisticas-link"
                icon={IconoEstadisticas}
                label="Estadísticas"
                notIncludedOnCurrentPlan={!hasModule(ModuleName.ESTADISTICAS)}
              />
            )}

            {/* Mis clientes — gate ESTRICTO de la superficie LISTADO (feature de paga controlada,
                sin fallback a freemium). */}
            {hasModule(ModuleName.PERFIL_CLIENTE_LISTADO) && (
              <NavButtonV3
                isExpanded={isExpanded}
                to="clientes"
                id="clientes-link"
                icon={ClientsIcon}
                label="Mis clientes"
              />
            )}

            {/* Conversaciones de WhatsApp (solo lectura) — mismo add-on que gatea la sección
                "Notificaciones a clientes" de Integraciones. Gate ESTRICTO (ocultar, sin upsell):
                este módulo no tiene ruta de compra desde el panel, así que `notIncludedOnCurrentPlan`
                mandaría a /suscripcion/... que solo existe para freemium. */}
            {hasModule(ModuleName.NOTIFICACIONES_CLIENTE_WHATSAPP) && (
              <NavButtonV3
                isExpanded={isExpanded}
                to="conversaciones"
                id="conversaciones-link"
                icon=""
                tablerIcon={<IconBrandWhatsapp color="#4298b5" stroke={2} />}
                label="Conversaciones"
              />
            )}

            {(isFreemium || hasModule(ModuleName.MENU)) && (
              <NavButtonV3
                isExpanded={isExpanded}
                to="menu"
                id="menu-link"
                icon={BtnMenu}
                label="Menú"
                notIncludedOnCurrentPlan={!hasModule(ModuleName.MENU)}
                showBadge={menuRolloutEligible && Boolean(hasModule(ModuleName.MENU))}
              />
            )}

            {(isFreemium || hasModule(ModuleName.CUPONES)) && (
              <NavButtonV3
                isExpanded={isExpanded}
                to="cupones"
                id="cupones-link"
                icon={CouponIcon}
                label="Cupones"
                notIncludedOnCurrentPlan={!hasModule(ModuleName.CUPONES)}
              />
            )}

            {(isFreemium || hasModule(ModuleName.SELF_SERVICE)) && (
              <NavButtonV3
                isExpanded={isExpanded}
                to="mesas"
                id="mesas-link"
                icon={BtnMesas}
                label="Mis Mesas"
                notIncludedOnCurrentPlan={!hasModule(ModuleName.SELF_SERVICE)}
              />
            )}

            {/* Pantallas (plick-screens) — SIN notIncludedOnCurrentPlan a propósito:
                esa prop rutea a /suscripcion/<path>, que solo existe para freemium
                (botón que fallaría en planes de pago sin el módulo). La página
                /pantallas se auto-gatea con preview + oferta (patrón Pedidos
                programados), así que la entrada es segura para todos. */}
            <NavButtonV3
              isExpanded={isExpanded}
              to="pantallas"
              id="pantallas-link"
              icon=""
              tablerIcon={<IconDeviceTv color="#4298b5" stroke={2} />}
              label="Pantallas"
            />

            {(isFreemium || hasModule(ModuleName.COBROS_EN_LINEA)) && !isFetching && commerce?.business_id && (
              <NavButtonV3
                isExpanded={isExpanded}
                to="pedidos-en-linea"
                id="pedidos-en-linea-link"
                icon={onlinePaymentIcon}
                label="Cobros en línea"
                notIncludedOnCurrentPlan={!hasModule(ModuleName.COBROS_EN_LINEA)}
              />
            )}

            {(isFreemium || hasModule(ModuleName.ANUNCIOS)) && (
              <NavButtonV3
                isExpanded={isExpanded}
                to="anuncios"
                id="anuncios-link"
                icon={BtnPubli}
                label="Crear anuncios"
                notIncludedOnCurrentPlan={!hasModule(ModuleName.ANUNCIOS)}
              />
            )}

            {(isFreemium || hasModule(ModuleName.COMPARTIR)) && (
              <NavButtonV3
                isExpanded={isExpanded}
                to="compartir-menu"
                id="compartir-link"
                icon={BtnQR}
                label="Compartir menú"
                notIncludedOnCurrentPlan={!hasModule(ModuleName.COMPARTIR)}
              />
            )}

            {(isFreemium || hasModule(ModuleName.LINK_PROFILE)) && (
              <NavButtonV3
                isExpanded={isExpanded}
                to="perfil-social"
                id="perfil-social-link"
                icon={BtnProfile}
                label="Perfil Social"
                notIncludedOnCurrentPlan={!hasModule(ModuleName.LINK_PROFILE)}
              />
            )}

            {(isFreemium || hasModule(ModuleName.ASISTENCIA_TECNICA)) && (
              <NavButtonV3
                isExpanded={isExpanded}
                to="asistencia"
                id="asistencia-link"
                icon={IconoQ}
                label="Asistencia Técnica"
                notIncludedOnCurrentPlan={!hasModule(ModuleName.ASISTENCIA_TECNICA)}
              />
            )}

            {/* Dropdown Ajustes */}
            <div className="flex flex-col" style={{ position: 'relative' }}>
              <button
                ref={settingsBtnRef}
                onClick={() => (isExpanded ? setIsConfigOpen(!isConfigOpen) : setSettingsFlyoutOpen((o) => !o))}
                title={!isExpanded ? 'Ajustes' : undefined}
                aria-haspopup={!isExpanded ? 'menu' : undefined}
                aria-expanded={!isExpanded ? settingsFlyoutOpen : isConfigOpen}
                data-press=""
                className={`group relative flex items-center py-[10px] pr-4 pl-[13px] text-[14px] border-l-[3px] ${
                  !isExpanded && settingsFlyoutOpen
                    ? 'text-color-primary bg-[#EDF5F7] border-transparent font-medium'
                    : 'text-color-primary font-medium border-transparent'
                }`}
                style={{
                  width: '100%',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  background: undefined,
                  outline: 'none',
                  borderLeftColor: 'transparent',
                  transition: 'background 160ms cubic-bezier(.4,0,.2,1), color 160ms, transform 80ms',
                }}
              >
                <img
                  src={BtnAjustes}
                  alt=""
                  role="presentation"
                  className="flex-shrink-0"
                  style={{ width: 18, height: 18 }}
                />
                <div
                  className={`overflow-hidden text-left transition-all ${isExpanded ? 'ml-3 w-full opacity-100' : 'w-0 opacity-0'}`}
                >
                  <span className="whitespace-nowrap" style={{ fontSize: 14, fontWeight: 500 }}>
                    Ajustes
                  </span>
                  {/* Descubrimiento: hay una funcionalidad nueva dentro (rollout impresion-ble). */}
                  {bleRolloutEligible && <NavNewPill />}
                </div>
                {isExpanded && (
                  <svg
                    width="10"
                    height="10"
                    viewBox="0 0 24 24"
                    fill="none"
                    aria-hidden="true"
                    className={`transition-transform flex-shrink-0 ml-auto ${isConfigOpen ? 'rotate-180' : ''}`}
                  >
                    <path
                      d="M6 9l6 6 6-6"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
                {/* Dot del trigger colapsado: azul = estás en una ruta de ajustes; acento =
                    hay una funcionalidad nueva esperando dentro (rollout impresion-ble). */}
                {!isExpanded && (isOnSettingsRoute || bleRolloutEligible) && (
                  <span
                    aria-hidden="true"
                    style={{
                      position: 'absolute',
                      right: 8,
                      top: 8,
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      background: isOnSettingsRoute ? '#4298B5' : '#FEAE33',
                    }}
                  />
                )}
              </button>

              {/* Flyout para sidebar colapsado */}
              {!isExpanded && settingsFlyoutOpen && (
                <div
                  data-settings-flyout
                  ref={(el) => {
                    if (!el || !settingsBtnRef.current) return;
                    const r1 = settingsBtnRef.current.getBoundingClientRect();
                    el.style.left = `${r1.right + 6}px`;
                    el.style.top = `${r1.top}px`;
                  }}
                  style={{
                    position: 'fixed',
                    background: '#FFFFFF',
                    border: '1px solid #E5ECEE',
                    borderRadius: 10,
                    boxShadow: '0 12px 28px rgba(37,55,70,.18)',
                    padding: 6,
                    minWidth: 220,
                    zIndex: 200,
                  }}
                >
                  <div
                    style={{
                      padding: '6px 12px 8px',
                      fontSize: 11,
                      color: '#6B6B6B',
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: '.05em',
                      borderBottom: '1px solid #E5ECEE',
                      marginBottom: 4,
                    }}
                  >
                    Ajustes
                  </div>
                  {(isFreemium || hasModule(ModuleName.DATOS_COMERCIO)) && (
                    <button
                      data-press=""
                      onClick={() => {
                        navigate(getNavigatePath(hasModule(ModuleName.DATOS_COMERCIO) || false, 'datos-del-comercio'));
                        setSettingsFlyoutOpen(false);
                      }}
                      style={{
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'center',
                        padding: '9px 12px',
                        border: 'none',
                        borderRadius: 6,
                        color: '#253746',
                        fontFamily: 'inherit',
                        fontSize: 14,
                        fontWeight: isSubActive('datos-del-comercio') ? 600 : 500,
                        cursor: 'pointer',
                        background: isSubActive('datos-del-comercio') ? '#EDF5F7' : 'transparent',
                        width: '100%',
                        textAlign: 'left',
                      }}
                      onMouseEnter={(e) => {
                        if (!isSubActive('datos-del-comercio'))
                          (e.currentTarget as HTMLButtonElement).style.background = '#EDF5F7';
                      }}
                      onMouseLeave={(e) => {
                        if (!isSubActive('datos-del-comercio'))
                          (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
                      }}
                    >
                      {isSubActive('datos-del-comercio') && (
                        <span
                          aria-hidden="true"
                          style={{
                            position: 'absolute',
                            left: 0,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            width: 3,
                            height: 20,
                            background: '#4298B5',
                            borderRadius: '0 3px 3px 0',
                          }}
                        />
                      )}
                      <span style={{ flex: 1 }}>Datos del comercio</span>
                      {!hasModule(ModuleName.DATOS_COMERCIO) && (
                        <Icon icon="solar:tag-price-bold" color="#FF3939" width="14" />
                      )}
                    </button>
                  )}
                  {(isFreemium || hasModule(ModuleName.HORARIOS)) && (
                    <button
                      data-press=""
                      onClick={() => {
                        navigate(getNavigatePath(hasModule(ModuleName.HORARIOS) || false, 'horarios'));
                        setSettingsFlyoutOpen(false);
                      }}
                      style={{
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'center',
                        padding: '9px 12px',
                        border: 'none',
                        borderRadius: 6,
                        color: '#253746',
                        fontFamily: 'inherit',
                        fontSize: 14,
                        fontWeight: isSubActive('horarios') ? 600 : 500,
                        cursor: 'pointer',
                        background: isSubActive('horarios') ? '#EDF5F7' : 'transparent',
                        width: '100%',
                        textAlign: 'left',
                      }}
                      onMouseEnter={(e) => {
                        if (!isSubActive('horarios'))
                          (e.currentTarget as HTMLButtonElement).style.background = '#EDF5F7';
                      }}
                      onMouseLeave={(e) => {
                        if (!isSubActive('horarios'))
                          (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
                      }}
                    >
                      {isSubActive('horarios') && (
                        <span
                          aria-hidden="true"
                          style={{
                            position: 'absolute',
                            left: 0,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            width: 3,
                            height: 20,
                            background: '#4298B5',
                            borderRadius: '0 3px 3px 0',
                          }}
                        />
                      )}
                      <span style={{ flex: 1 }}>Horarios</span>
                      {!hasModule(ModuleName.HORARIOS) && (
                        <Icon icon="solar:tag-price-bold" color="#FF3939" width="14" />
                      )}
                    </button>
                  )}
                  {(isFreemium || hasModule(ModuleName.TARIFAS)) && (
                    <button
                      data-press=""
                      onClick={() => {
                        navigate(getNavigatePath(hasModule(ModuleName.TARIFAS) || false, 'tarifas'));
                        setSettingsFlyoutOpen(false);
                      }}
                      style={{
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'center',
                        padding: '9px 12px',
                        border: 'none',
                        borderRadius: 6,
                        color: '#253746',
                        fontFamily: 'inherit',
                        fontSize: 14,
                        fontWeight: isSubActive('tarifas') ? 600 : 500,
                        cursor: 'pointer',
                        background: isSubActive('tarifas') ? '#EDF5F7' : 'transparent',
                        width: '100%',
                        textAlign: 'left',
                      }}
                      onMouseEnter={(e) => {
                        if (!isSubActive('tarifas'))
                          (e.currentTarget as HTMLButtonElement).style.background = '#EDF5F7';
                      }}
                      onMouseLeave={(e) => {
                        if (!isSubActive('tarifas'))
                          (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
                      }}
                    >
                      {isSubActive('tarifas') && (
                        <span
                          aria-hidden="true"
                          style={{
                            position: 'absolute',
                            left: 0,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            width: 3,
                            height: 20,
                            background: '#4298B5',
                            borderRadius: '0 3px 3px 0',
                          }}
                        />
                      )}
                      <span style={{ flex: 1 }}>Tarifas y entregas</span>
                      {!hasModule(ModuleName.TARIFAS) && (
                        <Icon icon="solar:tag-price-bold" color="#FF3939" width="14" />
                      )}
                    </button>
                  )}
                  {(isFreemium || hasModule(ModuleName.USUARIOS)) && (
                    <button
                      data-press=""
                      onClick={() => {
                        navigate(getNavigatePath(hasModule(ModuleName.USUARIOS) || false, 'usuarios'));
                        setSettingsFlyoutOpen(false);
                      }}
                      style={{
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'center',
                        padding: '9px 12px',
                        border: 'none',
                        borderRadius: 6,
                        color: '#253746',
                        fontFamily: 'inherit',
                        fontSize: 14,
                        fontWeight: isSubActive('usuarios') ? 600 : 500,
                        cursor: 'pointer',
                        background: isSubActive('usuarios') ? '#EDF5F7' : 'transparent',
                        width: '100%',
                        textAlign: 'left',
                      }}
                      onMouseEnter={(e) => {
                        if (!isSubActive('usuarios'))
                          (e.currentTarget as HTMLButtonElement).style.background = '#EDF5F7';
                      }}
                      onMouseLeave={(e) => {
                        if (!isSubActive('usuarios'))
                          (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
                      }}
                    >
                      {isSubActive('usuarios') && (
                        <span
                          aria-hidden="true"
                          style={{
                            position: 'absolute',
                            left: 0,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            width: 3,
                            height: 20,
                            background: '#4298B5',
                            borderRadius: '0 3px 3px 0',
                          }}
                        />
                      )}
                      <span style={{ flex: 1 }}>Alta de usuarios</span>
                      {!hasModule(ModuleName.USUARIOS) && (
                        <Icon icon="solar:tag-price-bold" color="#FF3939" width="14" />
                      )}
                    </button>
                  )}
                  {(isFreemium || hasModule(ModuleName.AJUSTES)) && (
                    <button
                      data-press=""
                      onClick={() => {
                        navigate(getNavigatePath(hasModule(ModuleName.AJUSTES) || false, 'ajustes'));
                        setSettingsFlyoutOpen(false);
                      }}
                      style={{
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'center',
                        padding: '9px 12px',
                        border: 'none',
                        borderRadius: 6,
                        color: '#253746',
                        fontFamily: 'inherit',
                        fontSize: 14,
                        fontWeight: isSubActive('ajustes') ? 600 : 500,
                        cursor: 'pointer',
                        background: isSubActive('ajustes') ? '#EDF5F7' : 'transparent',
                        width: '100%',
                        textAlign: 'left',
                      }}
                      onMouseEnter={(e) => {
                        if (!isSubActive('ajustes'))
                          (e.currentTarget as HTMLButtonElement).style.background = '#EDF5F7';
                      }}
                      onMouseLeave={(e) => {
                        if (!isSubActive('ajustes'))
                          (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
                      }}
                    >
                      {isSubActive('ajustes') && (
                        <span
                          aria-hidden="true"
                          style={{
                            position: 'absolute',
                            left: 0,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            width: 3,
                            height: 20,
                            background: '#4298B5',
                            borderRadius: '0 3px 3px 0',
                          }}
                        />
                      )}
                      <span style={{ flex: 1 }}>Ajustes</span>
                      {bleRolloutEligible && <NavNewPill />}
                      {!hasModule(ModuleName.AJUSTES) && (
                        <Icon icon="solar:tag-price-bold" color="#FF3939" width="14" />
                      )}
                    </button>
                  )}
                  <button
                    data-press=""
                    onClick={() => {
                      navigate('/integraciones');
                      setSettingsFlyoutOpen(false);
                    }}
                    style={{
                      position: 'relative',
                      display: 'flex',
                      alignItems: 'center',
                      padding: '9px 12px',
                      border: 'none',
                      borderRadius: 6,
                      color: '#253746',
                      fontFamily: 'inherit',
                      fontSize: 14,
                      fontWeight: isSubActive('integraciones') ? 600 : 500,
                      cursor: 'pointer',
                      background: isSubActive('integraciones') ? '#EDF5F7' : 'transparent',
                      width: '100%',
                      textAlign: 'left',
                    }}
                    onMouseEnter={(e) => {
                      if (!isSubActive('integraciones'))
                        (e.currentTarget as HTMLButtonElement).style.background = '#EDF5F7';
                    }}
                    onMouseLeave={(e) => {
                      if (!isSubActive('integraciones'))
                        (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
                    }}
                  >
                    {isSubActive('integraciones') && (
                      <span
                        aria-hidden="true"
                        style={{
                          position: 'absolute',
                          left: 0,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          width: 3,
                          height: 20,
                          background: '#4298B5',
                          borderRadius: '0 3px 3px 0',
                        }}
                      />
                    )}
                    <span>Integraciones</span>
                  </button>
                  <button
                    data-press=""
                    onClick={() => {
                      trackEvent(TRACK_EVENT_NAMES.FeedbackButtonClicked);
                      setShowFounderWarning(true);
                      setSettingsFlyoutOpen(false);
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '9px 12px',
                      border: 0,
                      borderRadius: 6,
                      color: '#253746',
                      fontFamily: 'inherit',
                      fontSize: 14,
                      fontWeight: 500,
                      cursor: 'pointer',
                      background: 'transparent',
                      width: '100%',
                      textAlign: 'left',
                    }}
                    onMouseEnter={(e) => {
                      (e.currentTarget as HTMLButtonElement).style.background = '#EDF5F7';
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
                    }}
                  >
                    <span>Ayúdanos a mejorar</span>
                  </button>
                </div>
              )}

              {isConfigOpen && isExpanded && (
                <div className="flex flex-col">
                  {[
                    {
                      show: isFreemium || hasModule(ModuleName.DATOS_COMERCIO),
                      path: 'datos-del-comercio',
                      label: 'Datos del comercio',
                      hasAccess: hasModule(ModuleName.DATOS_COMERCIO),
                      onClick: () =>
                        navigate(getNavigatePath(hasModule(ModuleName.DATOS_COMERCIO) || false, 'datos-del-comercio')),
                    },
                    {
                      show: isFreemium || hasModule(ModuleName.HORARIOS),
                      path: 'horarios',
                      label: 'Horarios',
                      hasAccess: hasModule(ModuleName.HORARIOS),
                      onClick: () => navigate(getNavigatePath(hasModule(ModuleName.HORARIOS) || false, 'horarios')),
                    },
                    {
                      show: isFreemium || hasModule(ModuleName.TARIFAS),
                      path: 'tarifas',
                      label: 'Tarifas y entregas',
                      hasAccess: hasModule(ModuleName.TARIFAS),
                      onClick: () => navigate(getNavigatePath(hasModule(ModuleName.TARIFAS) || false, 'tarifas')),
                    },
                    {
                      show: isFreemium || hasModule(ModuleName.USUARIOS),
                      path: 'usuarios',
                      label: 'Alta de usuarios',
                      hasAccess: hasModule(ModuleName.USUARIOS),
                      onClick: () => navigate(getNavigatePath(hasModule(ModuleName.USUARIOS) || false, 'usuarios')),
                    },
                    {
                      show: isFreemium || hasModule(ModuleName.AJUSTES),
                      path: 'ajustes',
                      label: 'Ajustes',
                      hasAccess: hasModule(ModuleName.AJUSTES),
                      // Pill "Nuevo" del rollout impresion-ble (control invitable + compatible).
                      isNew: bleRolloutEligible,
                      onClick: () => navigate(getNavigatePath(hasModule(ModuleName.AJUSTES) || false, 'ajustes')),
                    },
                    {
                      show: true,
                      path: 'integraciones',
                      label: 'Integraciones',
                      hasAccess: true,
                      onClick: () => navigate('/integraciones'),
                    },
                  ]
                    .filter((item) => item.show)
                    .map((item) => {
                      const active = isSubActive(item.path);
                      return (
                        <button
                          key={item.path}
                          data-press=""
                          data-nav-item=""
                          onClick={item.onClick}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: '7px 16px 7px 50px',
                            border: 'none',
                            outline: 'none',
                            background: active ? '#EDF5F7' : 'transparent',
                            color: '#253746',
                            fontSize: 13,
                            fontWeight: active ? 600 : 400,
                            cursor: 'pointer',
                            fontFamily: 'inherit',
                            width: '100%',
                            textAlign: 'left',
                            transition: 'background 160ms cubic-bezier(.4,0,.2,1)',
                          }}
                          onMouseEnter={(e) => {
                            if (!active) (e.currentTarget as HTMLButtonElement).style.background = '#EDF5F7';
                          }}
                          onMouseLeave={(e) => {
                            if (!active) (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
                          }}
                        >
                          <span style={{ flex: 1 }}>{item.label}</span>
                          {'isNew' in item && item.isNew && <NavNewPill />}
                          {!item.hasAccess && <Icon icon="solar:tag-price-bold" color="#FF3939" width="14" />}
                        </button>
                      );
                    })}

                  <button
                    data-press=""
                    data-nav-item=""
                    onClick={() => {
                      trackEvent(TRACK_EVENT_NAMES.FeedbackButtonClicked);
                      setShowFounderWarning(true);
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '7px 16px 7px 50px',
                      border: 'none',
                      boxShadow: 'none',
                      background: 'transparent',
                      color: '#253746',
                      fontSize: 13,
                      fontWeight: 400,
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                      width: '100%',
                      textAlign: 'left',
                      transition: 'background 160ms cubic-bezier(.4,0,.2,1)',
                    }}
                    onMouseEnter={(e) => {
                      (e.currentTarget as HTMLButtonElement).style.background = '#EDF5F7';
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
                    }}
                  >
                    <span>Ayúdanos a mejorar</span>
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Sección inferior: Ir a mi menú, Mi cuenta, Cerrar sesión */}
          <div
            className="flex-shrink-0 border-t flex flex-col"
            style={{ borderColor: '#E5ECEE', padding: '6px 6px', marginTop: 4 }}
          >
            {/* Ir a mi menú */}
            <Tooltip label="Ir a mi menú" enabled={!isExpanded}>
              {!isExpanded ? (
                <button
                  onClick={handleGoToMenu}
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 6,
                    background: 'transparent',
                    border: 0,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto',
                  }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLButtonElement).style.background = '#EDF5F7';
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
                  }}
                >
                  <Icon icon="tabler:external-link" color="#253746" width={16} />
                </button>
              ) : (
                <button
                  data-nav-item=""
                  onClick={handleGoToMenu}
                  className="flex items-center rounded-md w-full text-color-gray_ultra_dark"
                  style={{ padding: '7px 10px', background: 'transparent', border: 0, cursor: 'pointer' }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLButtonElement).style.background = '#EDF5F7';
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
                  }}
                >
                  <Icon icon="tabler:external-link" color="#6B6B6B" width="16" className="flex-shrink-0" />
                  <div className="ml-3 w-full opacity-100 overflow-hidden text-left">
                    <p style={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', color: '#253746' }}>
                      Ir a mi menú
                    </p>
                    <p style={{ fontSize: 11, whiteSpace: 'nowrap', color: '#6B6B6B' }}>Ver el menú público</p>
                  </div>
                </button>
              )}
            </Tooltip>

            {/* Mi cuenta */}
            <Tooltip label="Mi cuenta" enabled={!isExpanded}>
              {!isExpanded ? (
                <button
                  onClick={() => navigate('mi-cuenta')}
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 6,
                    background: 'transparent',
                    border: 0,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto',
                  }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLButtonElement).style.background = '#EDF5F7';
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
                  }}
                >
                  <Icon icon="fa-solid:user" color="#253746" width={16} />
                </button>
              ) : (
                <button
                  data-nav-item=""
                  onClick={() => navigate('mi-cuenta')}
                  className="flex items-center rounded-md w-full text-color-gray_ultra_dark"
                  style={{ padding: '7px 10px', background: 'transparent', border: 0, cursor: 'pointer' }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLButtonElement).style.background = '#EDF5F7';
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
                  }}
                >
                  <Icon icon="fa-solid:user" color="#6B6B6B" width="16" className="flex-shrink-0" />
                  <div className="ml-3 w-full opacity-100 overflow-hidden text-left">
                    <p style={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', color: '#253746' }}>Mi cuenta</p>
                    <p style={{ fontSize: 11, whiteSpace: 'nowrap', color: '#6B6B6B' }}>Perfil y suscripción</p>
                  </div>
                </button>
              )}
            </Tooltip>

            {/* Cerrar sesión */}
            <Tooltip label="Cerrar sesión" enabled={!isExpanded}>
              {!isExpanded ? (
                <button
                  onClick={logout}
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 6,
                    background: 'transparent',
                    border: 0,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto',
                  }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLButtonElement).style.background = '#FFF5F5';
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
                  }}
                >
                  <Icon icon="tabler:logout" color="#FF3939" width={16} />
                </button>
              ) : (
                <button
                  onClick={logout}
                  className="flex items-center rounded-md w-full"
                  style={{ padding: '7px 10px', background: 'transparent', border: 0, cursor: 'pointer' }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLButtonElement).style.background = '#FFF5F5';
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
                  }}
                >
                  <Icon icon="tabler:logout" color="#FF3939" width="16" className="flex-shrink-0" />
                  <div className="ml-3 w-full opacity-100 overflow-hidden text-left">
                    <span style={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', color: '#FF3939' }}>
                      Cerrar sesión
                    </span>
                  </div>
                </button>
              )}
            </Tooltip>
          </div>
        </NavBarContainerV2>
      )}

      {showFounderWarning && (
        <FounderContactModal
          onClose={() => setShowFounderWarning(false)}
          onContinue={() => {
            setShowFounderWarning(false);
            setShowFounderForm(true);
          }}
        />
      )}
      {showFounderForm && <FounderMessageForm commerce={commerce} onClose={() => setShowFounderForm(false)} />}
    </NavBarBackground>
  );
};

export default NavBarDesktopV3;
